/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FieldArrayRenderProps, useFormikContext } from 'formik'
import { useServiceContext } from '../../root'
import { WF_STEP_PROCESSING_TYPE } from '../../constants/workflow-steps'
import { Select } from '../Select/Select'
import { ErrorMessageStyled } from '../shared/ErrorMessage'
import { SelectUserGroup } from '../pickers/SelectUserGroup'
import { SelectStepLabel } from '../pickers/SelectStepLabel'

/**
 * UserGroupSteps Component
 * Ported from lego/page/browser/request-change/UserGroupSteps.jsx
 *
 * Manages workflow step sequences with users/groups and processing types
 */

interface UserGroupStepsProps {
  arrayHelpers: FieldArrayRenderProps
  values: any[]
  nofallTxt: string
  labelTxt: string
  autoAddUser?: boolean | number
  disabled?: boolean
  increaseUserGroupWidth?: boolean
  isFromManageWFModals?: boolean
}

export const UserGroupSteps: React.FC<UserGroupStepsProps> = ({
  arrayHelpers,
  values,
  nofallTxt,
  labelTxt,
  autoAddUser,
  disabled = false,
  increaseUserGroupWidth = false
}) => {
  const { t: translate } = useTranslation()
  const context = useServiceContext()
  const formikContext = useFormikContext()
  const [initialized, setInitialized] = useState(false)
  const [preselectedUser, setPreselectedUser] = useState<any>(null)

  const orderTypes = [
    { value: WF_STEP_PROCESSING_TYPE.SERIAL, label: translate('ONE_AT_A_TIME') },
    { value: WF_STEP_PROCESSING_TYPE.PARALLEL, label: translate('ALL_AT_ONCE') },
    { value: WF_STEP_PROCESSING_TYPE.N_OF_ALL, label: translate('N_OF_ALL') }
  ]

  useEffect(() => {
    if (!initialized) {
      const promises: Promise<any>[] = []
      promises.push(context.api.users.getAssembledUsersGroups())
      promises.push(context.api.settings.getCrTeamTaskStepLabels())

      Promise.all(promises)
        .then((results: any[]) => {
          const availableUsersGroups = results[0].merged.filter((user: any) => {
            return !['INSPECTOR', 'AUDITOR'].includes(user.role)
          })

          let usr: any = null
          const currentUser = context.service.auth.getUser()
          availableUsersGroups.forEach((user: any) => {
            if (
              (typeof autoAddUser === 'boolean' &&
                autoAddUser &&
                currentUser &&
                user.id === currentUser.id &&
                user.systemHelperType === 'USER') ||
              (typeof autoAddUser === 'number' &&
                autoAddUser > 0 &&
                user.id === autoAddUser &&
                user.systemHelperType === 'USER')
            ) {
              setPreselectedUser(user)
              usr = user
            }
          })

          if (!Array.isArray(values) || values.length === 0) {
            addStep(autoAddUser && usr ? [usr] : undefined)
          }
        })
        .finally(() => {
          setInitialized(true)
        })
    }
  }, [initialized])

  const addStep = (users?: any[]) => {
    const newStep = {
      processingType: WF_STEP_PROCESSING_TYPE.SERIAL,
      nofAllValue: '',
      merged: users && Array.isArray(users) ? users : [],
      taskTeamStepLabel: null
    }
    arrayHelpers.push(newStep)
  }

  const removeStep = (index: number) => {
    arrayHelpers.remove(index)
  }

  const updateStep = (newData: any, index: number) => {
    arrayHelpers.replace(index, newData)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, maxLength: number) => {
    const { keyCode } = event
    const target = event.target as HTMLInputElement

    // Allow numbers, backspace, and arrow keys
    const isNumberKey = (keyCode >= 48 && keyCode <= 57) || (keyCode >= 96 && keyCode <= 105)
    const isControlKey = keyCode === 8 || keyCode === 37 || keyCode === 38 || keyCode === 39 || keyCode === 40

    if (!isNumberKey && !isControlKey) {
      event.preventDefault()
      return
    }

    // Check max length
    if (target.value.length >= maxLength && isNumberKey) {
      event.preventDefault()
    }
  }

  return (
    <div className="form-group">
      <label>{translate(labelTxt)}</label>
      {values && values.length === 0 ? <ErrorMessageStyled name={`${arrayHelpers.name}`} /> : null}

      <div className="custom-approvers-table">
        {values && values.length > 0 && (
          <div className="table-header" style={{ display: 'flex' }}>
            <span className="h steps ta-center">{translate('STEP')}</span>
            <span className="h customlabel">{translate('CR_CUSTOM_STEP_LABEL')}</span>
            <span className="h usersgroups">{translate('USERS_GROUPS')}</span>
            <span className="h order">{translate('ORDER')}</span>
          </div>
        )}

        {values &&
          values.map((item, index) => (
            <div key={index} className="table-row" style={{ display: 'flex', padding: '5px 0' }}>
              <span className="h steps ta-center py5">{index + 1}</span>

              <span className="h customlabel">
                <SelectStepLabel
                  name={`${arrayHelpers.name}.${index}.taskTeamStepLabel`}
                  value={item.taskTeamStepLabel}
                  onChange={(option) => {
                    item.taskTeamStepLabel = option ? { id: option.value, text: option.label } : null
                    updateStep(item, index)
                  }}
                />
              </span>

              <span style={{ maxWidth: increaseUserGroupWidth ? '255px' : '181px' }} className="h usersgroups">
                <SelectUserGroup
                  name={`${arrayHelpers.name}.${index}.merged`}
                  value={item.merged || []}
                  placeholder={translate('SELECT_USERS/GROUPS...')}
                  isMulti={true}
                  isDisabled={disabled}
                  onChange={(value) => {
                    item.merged = value || []
                    updateStep(item, index)
                    // Trigger validation after change
                    if (!value || (Array.isArray(value) && value.length === 0)) {
                      formikContext.setFieldTouched(`${arrayHelpers.name}.${index}.merged`, true)
                    } else {
                      formikContext.setFieldTouched(`${arrayHelpers.name}.${index}.merged`, false)
                    }
                  }}
                />
                <ErrorMessageStyled name={`${arrayHelpers.name}.${index}.merged`} />
              </span>

              <span className="h order">
                <Select
                  name={`${arrayHelpers.name}.${index}.processingType`}
                  value={item.processingType}
                  options={orderTypes}
                  isClearable={false}
                  onChange={(option: any) => {
                    item.processingType = option ? option.value : null
                    updateStep(item, index)
                  }}
                  id="795f0a8a-f8b3-47d8-81ca-b3b3ab065e27"
                />
                <ErrorMessageStyled name={`${arrayHelpers.name}.${index}.processingType`} />

                {item.processingType === WF_STEP_PROCESSING_TYPE.N_OF_ALL && (
                  <div className="nofall-wrapper hform-group pt5">
                    <input
                      onKeyDown={(e) => handleKeyDown(e, 9)}
                      style={{ border: '1px solid #ccc', borderRadius: '2px' }}
                      disabled={disabled}
                      readOnly={disabled}
                      type="number"
                      name={`${arrayHelpers.name}.${index}.nofAllValue`}
                      onChange={(e) => {
                        item.nofAllValue = e.target.value
                        updateStep(item, index)
                      }}
                      onBlur={() => {
                        if (item.nofAllValue === '' || item.nofAllValue <= 0) {
                          formikContext.setFieldTouched(`${arrayHelpers.name}.${index}.nofAllValue`, true)
                        } else {
                          formikContext.setFieldTouched(`${arrayHelpers.name}.${index}.nofAllValue`, false)
                        }
                      }}
                      value={item.nofAllValue}
                      className="md-input nofall-input"
                    />{' '}
                    {translate(nofallTxt)}
                    <div className="pt5 normal-line-height" style={{ whiteSpace: 'normal', width: '140px!important' }}>
                      <ErrorMessageStyled name={`${arrayHelpers.name}.${index}.nofAllValue`} />
                    </div>
                  </div>
                )}
              </span>

              {!disabled && (
                <a
                  className="close mr1"
                  onClick={(e) => {
                    e.preventDefault()
                    removeStep(index)
                  }}
                />
              )}
            </div>
          ))}
      </div>

      {!disabled && (
        <button
          data-action="add-step"
          className="btn btn-chita btn-add-step btn-second at_btnAddStepApprover"
          type="button"
          onClick={() => addStep()}
        >
          {translate('ADD_STEP')}
        </button>
      )}
    </div>
  )
}

export default UserGroupSteps
